/**
 * Esquema mínimo de `flow_config` alineado con las 5 capas del Agent Builder:
 * trigger → contexto → lógica → acción → outcome.
 *
 * Compatible con @xyflow/react: `nodes` y `edges` son los mismos que expone React Flow.
 *
 * @module agentFlowSchema
 */

/** @typedef {'trigger'|'context'|'logic'|'action'|'outcome'} AgentFlowLayer */

/**
 * Catálogo de capas (uso en UI, analytics y validación).
 * @type {Readonly<{ TRIGGER: 'trigger', CONTEXT: 'context', LOGIC: 'logic', ACTION: 'action', OUTCOME: 'outcome' }>}
 */
export const AGENT_FLOW_LAYERS = Object.freeze({
  TRIGGER: 'trigger',
  CONTEXT: 'context',
  LOGIC: 'logic',
  ACTION: 'action',
  OUTCOME: 'outcome',
});

/**
 * Tipos de nodo visuales actuales (MVP). Futuros: `action_whatsapp`, `logic_score`, etc.
 * @typedef {'start'|'message'|'condition'|'end'} AgentFlowNodeType
 */

/** Capa por defecto según tipo de nodo (MVP). */
export const DEFAULT_LAYER_BY_NODE_TYPE = Object.freeze({
  start: AGENT_FLOW_LAYERS.TRIGGER,
  message: AGENT_FLOW_LAYERS.ACTION,
  condition: AGENT_FLOW_LAYERS.LOGIC,
  end: AGENT_FLOW_LAYERS.OUTCOME,
});

/**
 * Qué activa el flujo (capa 1). Ampliar cuando exista catálogo backend.
 * @typedef {Object} TriggerSpec
 * @property {'event'|'schedule'|'rule'|'manual'} kind
 * @property {string|null} [ref] id semántico del catálogo, ej. `payment_due_tomorrow`
 * @property {string} [summary] texto libre para el diseñador
 */

/**
 * Qué datos entran al flujo (capa 2). El motor resuelve valores; aquí solo referencias.
 * @typedef {Object} ContextBinding
 * @property {string} key nombre en el contexto de ejecución, ej. `saldo_actual`
 * @property {string} source origen declarado, ej. `crm.profile`, `ledger.balance`
 */

/**
 * @typedef {Object} ContextSpec
 * @property {ContextBinding[]} bindings
 */

/**
 * Metadatos del flujo no ligados a un nodo concreto (triggers/contexto declarativo).
 * @typedef {Object} FlowMeta
 * @property {TriggerSpec} [trigger]
 * @property {ContextSpec} [context]
 */

/**
 * @typedef {Object} FlowConfig
 * @property {number} version versión del esquema (≥2 incluye meta + layer en nodos)
 * @property {FlowMeta} [meta]
 * @property {import('@xyflow/react').Node[]} nodes
 * @property {import('@xyflow/react').Edge[]} edges
 */

/**
 * Documento persistido (fila). El backend puede añadir `company_id`, etc.
 * @typedef {Object} AgentFlowRow
 * @property {string} id
 * @property {string} name
 * @property {string} status
 * @property {FlowConfig} flow_config
 * @property {string} [createdAt]
 * @property {string} [updatedAt]
 */

export const FLOW_CONFIG_VERSION = 2;

const defaultMeta = () => ({
  trigger: {
    kind: 'manual',
    ref: null,
    summary: '',
  },
  context: {
    bindings: [],
  },
});

/** Meta vacío (útil si el editor monta antes de hidratar). */
export function getDefaultFlowMeta() {
  return defaultMeta();
}

/**
 * @param {AgentFlowNodeType} nodeType
 * @returns {AgentFlowLayer}
 */
export function defaultLayerForNodeType(nodeType) {
  return DEFAULT_LAYER_BY_NODE_TYPE[nodeType] || AGENT_FLOW_LAYERS.LOGIC;
}

/**
 * Rellena campos por capa en `data` si faltan (migración suave).
 * @param {import('@xyflow/react').Node} n
 * @returns {import('@xyflow/react').Node}
 */
export function enrichNodeData(n) {
  const type = n.type || 'condition';
  const layer = n.data?.layer || defaultLayerForNodeType(type);
  const data = { ...n.data, layer };

  if (type === 'message') {
    if (!data.actionKind) data.actionKind = 'dynamic_message';
    if (data.templateName === undefined) data.templateName = '';
    if (data.noteBody === undefined) data.noteBody = '';
    if (data.reminderLabel === undefined) data.reminderLabel = '';
    if (data.blockDurationHours === undefined) data.blockDurationHours = '';
  }
  if (type === 'condition') {
    if (!data.logicKind) data.logicKind = 'simple';
  }
  if (type === 'end') {
    if (!data.outcomeCategory) data.outcomeCategory = 'other';
    if (data.outcomeKey === undefined) data.outcomeKey = '';
  }
  if (type === 'start') {
    if (data.triggerHint === undefined) data.triggerHint = '';
  }

  return { ...n, data };
}

/**
 * Normaliza a `FLOW_CONFIG_VERSION`, rellena `meta` y asegura `data.layer` en cada nodo.
 * @param {Partial<FlowConfig>|null|undefined} raw
 * @returns {FlowConfig}
 */
export function migrateFlowConfig(raw) {
  const base = raw && typeof raw === 'object' ? raw : {};
  const nodesIn = Array.isArray(base.nodes) ? base.nodes : [];
  const edges = Array.isArray(base.edges) ? base.edges : [];

  const nodes = nodesIn.map((n) => enrichNodeData(n));

  const prevMeta = base.meta && typeof base.meta === 'object' ? base.meta : {};
  const trigger = {
    ...defaultMeta().trigger,
    ...prevMeta.trigger,
  };
  const context = {
    ...defaultMeta().context,
    ...prevMeta.context,
    bindings: Array.isArray(prevMeta.context?.bindings)
      ? prevMeta.context.bindings
      : defaultMeta().context.bindings,
  };

  return {
    version: FLOW_CONFIG_VERSION,
    meta: { ...prevMeta, trigger, context },
    nodes,
    edges,
  };
}

/**
 * @returns {FlowConfig}
 */
export function createDefaultFlowConfig() {
  return migrateFlowConfig({
    version: 1,
    nodes: [
      {
        id: 'start-1',
        type: 'start',
        position: { x: 120, y: 80 },
        data: { label: 'Inicio' },
      },
    ],
    edges: [],
  });
}

/**
 * Payload listo para guardar (mantiene `meta` y versión).
 * @param {{ nodes: import('@xyflow/react').Node[], edges: import('@xyflow/react').Edge[], meta?: FlowMeta }} input
 * @returns {FlowConfig}
 */
export function buildFlowConfigForSave({ nodes, edges, meta }) {
  return migrateFlowConfig({
    version: FLOW_CONFIG_VERSION,
    meta: meta || defaultMeta(),
    nodes,
    edges,
  });
}
